import { PlacementId, CustomListItemId, CustomListKey } from "@sp-crm/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AddClientButton } from "components/clients/add-client-button/add-client-button";
import { PageHeader } from "components/layout";
import { produce } from "immer";
import { PlacementPage } from "components/feature";
import * as React from "react";
import { useDispatch } from "react-redux";
import { useCustomList } from "store/selectors/bridge";
import { PipelineState, usePipelineState } from "store/selectors/hooks";
import { handleEvent } from "util/user-events";
import { updateClient } from "../../store/actions";
import { reactivateClientWithoutNavigation } from "../../store/actions/clients";
import { promptForStatusIdChange } from "../../util/clientStatus";
import { ClientSearch } from "../clients/client-search/client-search";
import { trpc } from "../rpc/trpc";
import { ClientCardv2 } from "./card";
import { PipelineInner } from "./pipeline-inner";
import {
    clientCardType,
    type ClientForDetails,
    type ClientsSearchResult,
    type GlobalClientSearchCriteria,
    type PipelineCardRenderProps,
    type PipelineConfig,
    type PipelineItem,
} from "./types";

function useClientColumnQuery(
    statusId: CustomListItemId,
    searchCriteria: Record<string, unknown>,
): { data: PipelineItem[] | undefined } {
    const trpcClient = trpc.useTRPC();
    const normalizedCriteria = {
        ...searchCriteria,
        status: statusId as string,
    };
    const query = useQuery({
        ...trpcClient.clients.getClientsForDetails.queryOptions(
            normalizedCriteria as GlobalClientSearchCriteria,
        ),
    });
    return {
        data: query.data ? (query.data.clients as PipelineItem[]) : undefined,
    };
}

function useClientPipelineConfig(): PipelineConfig {
    const dispatch = useDispatch();
    const queryClient = useQueryClient();
    const { customList: clientStatusList, getListItemKey } = useCustomList(
        CustomListKey.ClientStatus,
    );

    const onDragDrop = React.useCallback(
        async (
            entityId: string,
            oldStatusId: CustomListItemId,
            newStatusId: CustomListItemId,
            searchCriteria: Record<string, unknown>,
        ) => {
            const oldStatus = getListItemKey(oldStatusId);
            const newStatus = getListItemKey(newStatusId);
            handleEvent("client-status-change", {
                source: "pipeline",
                oldStatus,
                newStatus,
            });
            const statusOrReactivate = await promptForStatusIdChange(
                clientStatusList,
                oldStatusId,
                newStatusId,
            );

            const oldQueryKey = [
                ["clients", "getClientsForDetails"],
                {
                    input: { ...searchCriteria, status: oldStatusId },
                    type: "query",
                },
            ];
            const oldQueryData: ClientsSearchResult =
                queryClient.getQueryData(oldQueryKey);
            const oldClient = oldQueryData?.clients.find(
                (c: ClientForDetails) => c.id === entityId,
            );

            if (oldClient) {
                queryClient.setQueryData(
                    oldQueryKey,
                    (oldStatusData: ClientsSearchResult) => {
                        return produce(oldStatusData, (draft: ClientsSearchResult) => {
                            draft.clients = draft.clients.filter(
                                (client: ClientForDetails) => client.id !== entityId,
                            );
                            draft.total = draft.clients.length;
                        });
                    },
                );
                const newQueryKey = [
                    ["clients", "getClientsForDetails"],
                    {
                        input: { ...searchCriteria, status: newStatusId },
                        type: "query",
                    },
                ];
                queryClient.setQueryData(
                    newQueryKey,
                    (newStatusData: ClientsSearchResult) => {
                        return produce(newStatusData, (draft: ClientsSearchResult) => {
                            draft.clients.unshift(oldClient);
                            draft.total = draft.clients.length;
                        });
                    },
                );
            }
            if (statusOrReactivate === "statusChange") {
                await updateClient(
                    entityId as PlacementId,
                    "statusListItemId",
                    newStatusId,
                    dispatch,
                );
            } else {
                await reactivateClientWithoutNavigation(
                    entityId as PlacementId,
                    dispatch,
                );
            }
            queryClient.refetchQueries({
                queryKey: [["clients", "getClientsForDetails"]],
            });
        },
        [clientStatusList, dispatch, getListItemKey, queryClient],
    );

    const renderCard = React.useCallback(
        (item: PipelineItem, renderProps: PipelineCardRenderProps) => (
            <ClientCardv2
                client={item as ClientForDetails}
                highlightCard={renderProps.highlightCard}
                pendingCard={renderProps.pendingCard}
                showAssignedTo={renderProps.showAssignedTo}
                getClientStatus={renderProps.getStatusDescription}
            />
        ),
        [],
    );

    return React.useMemo(
        () => ({
            customListKey: CustomListKey.ClientStatus,
            cardType: clientCardType,
            useColumnQuery: useClientColumnQuery,
            onDragDrop,
            renderCard,
        }),
        [onDragDrop, renderCard],
    );
}

export const PipelineV2: React.FunctionComponent = () => {
    const pipelineState: PipelineState = usePipelineState();
    const config = useClientPipelineConfig();

    return (
        <PlacementPage>
            <div className="flex flex-col md:inset-0 md:absolute">
                <PageHeader title="Client Pipeline">
                    <AddClientButton />
                </PageHeader>
                <div className="px-4 py-2">
                    <ClientSearch includeCardSort />
                </div>
                <div className="pipeline-wrapper bg-brand-700 flex px-4 py-2 flex-1 h-screen md:h-auto md:overflow-hidden">
                    {pipelineState && pipelineState.isReady ? (
                        <PipelineInner
                            config={config}
                            searchCriteria={pipelineState.searchCriteria}
                        />
                    ) : null}
                </div>
            </div>
        </PlacementPage>
    );
};
